import api from './api';

export interface DailyEntry {
  id: number;
  project_id: number;
  project_name: string;
  hours_worked: number;
  entry_date: string;
  description?: string;
  created_at: string;
}

export interface WeeklyDay {
  date: string;
  day: string;
  entries: DailyEntry[];
  totalHours: number;
  goalHours: number;
}

// Format date as YYYY-MM-DD
function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Get Monday of the week containing the given date
function getWeekStart(date: Date = new Date()): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

// Normalize a raw API time_entry object to DailyEntry
function normalizeTimeEntry(raw: any): DailyEntry {
  return {
    id: raw.id,
    project_id: raw.project_id,
    project_name: raw.project?.name || 'Unknown',
    hours_worked: parseFloat(raw.hours_worked),
    entry_date: raw.entry_date,
    description: raw.description,
    created_at: raw.created_at,
  };
}

// Build weekly summary from a flat list of raw entries
function buildWeeklyData(entries: any[], weekStart: Date): WeeklyDay[] {
  const GOAL_HOURS = 8;
  const days: WeeklyDay[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    const dateStr = formatDate(date);
    const dayEntries = entries
      .filter((e) => e.entry_date === dateStr)
      .map(normalizeTimeEntry);

    const totalHours = dayEntries.reduce((sum, e) => sum + e.hours_worked, 0);

    days.push({
      date: dateStr,
      day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      entries: dayEntries,
      totalHours,
      goalHours: GOAL_HOURS,
    });
  }

  return days;
}

export const dashboardService = {
  createEntry: async (data: {
    project_id: number;
    hours_worked: number;
    entry_date: string;
    description?: string;
  }): Promise<DailyEntry> => {
    const response = await api.post<{ time_entry: any }>('/time_entries', {
      time_entry: data,
    });
    return normalizeTimeEntry(response.data.time_entry);
  },
};

export const dashboardAPI = {
  // Fetch time entries for a specific date range (daily use: pass same date for start/end)
  getDailyEntries: async (
    userId: number,
    startDate: string,
    endDate: string
  ): Promise<DailyEntry[]> => {
    const response = await api.get<{ time_entries: any[] }>('/time_entries', {
      params: {
        user_id: userId,
        start_date: startDate,
        end_date: endDate,
      },
    });
    return response.data.time_entries.map(normalizeTimeEntry);
  },

  // Fetch weekly summary for the week containing the given date
  getWeeklySummary: async (
    userId: number,
    startDate?: string,
    endDate?: string
  ): Promise<WeeklyDay[]> => {
    const weekStart = startDate ? new Date(startDate) : getWeekStart();
    const weekEnd = endDate
      ? new Date(endDate)
      : (() => {
          const d = new Date(weekStart);
          d.setDate(d.getDate() + 6);
          return d;
        })();

    const response = await api.get<{ time_entries: any[] }>('/time_entries', {
      params: {
        user_id: userId,
        start_date: formatDate(weekStart),
        end_date: formatDate(weekEnd),
      },
    });

    return buildWeeklyData(response.data.time_entries, weekStart);
  },
};
